import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getHomepageSectionML } from '../services/api';
import { ChevronRight, Target, Users, Globe, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import AboutSectionML from '../components/sections/AboutSectionML';

const AboutPage = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      const lang = ['pt','en','fr','ar','zh'].includes(language) ? language : 'en';
      const r = await getHomepageSectionML('about', lang);
      if (r.success) setContent(r.data);
    };
    load();
  }, [language]);

  const values = [
    { icon: Target, title: 'Mission-Driven', desc: 'Every engagement is structured to deliver measurable outcomes for our partners and stakeholders.' },
    { icon: Users, title: 'People First', desc: 'We build lasting relationships based on trust, transparency and mutual respect across cultures.' },
    { icon: Globe, title: 'Global Mindset', desc: 'Operating across 3 continents with deep local knowledge in Dubai, Angola and European markets.' },
    { icon: Award, title: 'Excellence', desc: 'ISO-compliant processes and international standards applied to every project we undertake.' }
  ];

  const timeline = [
    { year: '2008', event: 'Founded in Luanda, Angola. First operations in business architecture and trading.' },
    { year: '2012', event: 'Expanded commodity trading to China. Partnership with manufacturing partners.' },
    { year: '2018', event: 'Established Dubai headquarters. MENA market entry consulting launched.' },
    { year: '2021', event: 'PEGADO Motors vehicles begin circulating across African markets.' },
    { year: '2023', event: 'Digital transformation. Launch of Grupo BMP enterprise platform.' },
    { year: '2026', event: 'Global operations spanning UAE, Angola, Portugal and China.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero — extends behind the transparent fixed header on this route */}
      <section className="bg-[#023E8A] text-white pt-44 pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#48CAE4]" />
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#48CAE4]">About Grupo BMP</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight mb-6">
              {content?.heading || 'Operational Excellence in Global Markets'}
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl">
              Since 2008, we have been structuring, implementing and scaling business operations across emerging and developed markets.
            </p>
          </div>
        </div>
      </section>

      {/* About Section (from CMS) */}
      <AboutSectionML />

      {/* Our Values */}
      <section className="py-24 bg-[#f8f9fa]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#00B4D8]" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8]">Our Values</p><div className="h-px w-8 bg-[#00B4D8]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                <div className="bg-[#023E8A]/5 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#FF6B35] transition-colors">
                  <v.icon className="w-7 h-7 text-[#023E8A] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2D42] mb-2">{v.title}</h3>
                <p className="text-sm text-[#6C757D] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-4">Our Journey</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">17+ Years of Growth</h2>
          </div>
          <div className="space-y-0">
            {timeline.map((t, i) => (
              <div key={i} className="flex gap-6 group">
                <div className="flex flex-col items-center">
                  <div className="w-4 h-4 rounded-full bg-[#FF6B35] group-hover:scale-125 transition-transform" />
                  {i < timeline.length - 1 && <div className="w-0.5 h-full bg-gray-200 min-h-[60px]" />}
                </div>
                <div className="pb-8">
                  <span className="text-2xl font-extrabold text-[#023E8A]">{t.year}</span>
                  <p className="text-sm text-[#6C757D] mt-1 leading-relaxed">{t.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#023E8A] py-16">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">Ready to Work With Us?</h3>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">Let us help you structure and scale your business operations globally.</p>
          <Link to="/contact">
            <button className="bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold px-10 py-4 rounded-xl text-lg transition-all hover:scale-105 inline-flex items-center gap-2">
              Contact Us <ArrowRight className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
